<template>
  <div class="km-page">
    <header class="km-top">
      <button class="km-back" @click="back()" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="km-top__title">
        <span class="km-top__dot"></span>
        <h1>{{ $t('my.sys_notice') }}</h1>
        <span v-if="unreadCount" class="km-top__badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
      </div>
      <button
        class="km-top__mark"
        :disabled="unreadCount === 0 || marking"
        @click="markAll"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        <span>{{ marking ? 'Marking…' : 'Mark all read' }}</span>
      </button>
    </header>

    <main class="km-main">
      <van-pull-refresh v-model="isLoading" @refresh="onRefresh">
        <section class="km-section">
          <div v-if="noticeList.length === 0 && !isLoading" class="km-empty">
            <div class="km-empty__emoji">📭</div>
            <h3>Nothing new here yet</h3>
            <p>System notices and announcements will appear here.</p>
          </div>
          <article
            v-for="(v, key) in noticeList" :key="v.id || key"
            class="km-notice"
            :class="{ 'km-notice--unread': !v.read }"
            @click="markOneRead(v)"
          >
            <div class="km-notice__head">
              <span class="km-notice__ico">🔔</span>
              <h3 class="km-notice__title">{{ v.name }}</h3>
              <span v-if="!v.read" class="km-notice__dot" aria-label="Unread"></span>
            </div>
            <p class="km-notice__body">{{ v.text }}</p>
            <div class="km-notice__time">{{ v.create_time }}</div>
          </article>
        </section>
      </van-pull-refresh>
    </main>
  </div>
</template>

<script>
export default {
  data() {
    return { isLoading: false, notice: [], marking: false };
  },
  computed: {
    noticeList(){
      const list = Array.isArray(this.notice) ? this.notice : Object.values(this.notice || {});
      return list.slice().sort((a, b) => (Number(b.created_at) || 0) - (Number(a.created_at) || 0));
    },
    unreadCount(){ return this.noticeList.filter(n => !n.read).length; }
  },
  methods: {
    back(){ return window.history.back(); },
    getNoticeList(){
      this.$http({ method: 'get', url: 'sys_get_notice_list' }).then(res => {
        this.notice = Array.isArray(res.data) ? res.data : Object.values(res.data || {});
      });
    },
    onRefresh(){
      this.getNoticeList();
      setTimeout(() => { this.isLoading = false; }, 500);
    },
    markOneRead(n){
      if (!n || !n.id || n.read) return;
      this.$set(n, 'read', true);
      this.$http({ method: 'post', url: 'notice_mark_read', data: { notice_id: n.id } });
    },
    markAll(){
      if (this.unreadCount === 0 || this.marking) return;
      this.marking = true;
      // Optimistic UI
      this.noticeList.forEach(n => this.$set(n, 'read', true));
      this.$http({ method: 'post', url: 'notice_mark_all_read' }).then(res => {
        this.marking = false;
        if (res && res.code === 200) {
          this.$toast.success ? this.$toast.success('All read 💜') : this.$toast('All read');
        }
      }).catch(() => { this.marking = false; });
    }
  },
  created(){ this.getNoticeList(); }
};
</script>

<style lang="less" scoped>
@import "./_km-page.less";

.km-top__title { position: relative; }
.km-top__badge {
  margin-left: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 11px; font-weight: 800; letter-spacing: 0;
  box-shadow: 0 3px 8px rgba(255,77,141,.35);
}
.km-top__mark {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1.5px solid rgba(160,32,240,.2);
  background: #fff; color: #a020f0;
  font-family: inherit; font-size: 12px; font-weight: 800;
  cursor: pointer; transition: all .15s;
}
.km-top__mark:hover:not(:disabled) { border-color: #ff4d8d; color: #ff4d8d; }
.km-top__mark:active:not(:disabled) { transform: scale(.96); }
.km-top__mark:disabled { opacity: .45; cursor: default; border-color: rgba(26,16,51,.1); color: #8d7fa6; }

.km-notice {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 16px;
  border: 1px solid rgba(255,77,141,.12);
  box-shadow: 0 6px 18px rgba(255,77,141,.08), inset 0 1px 0 rgba(255,255,255,.9);
  padding: 16px 18px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: border-color .15s, transform .15s, box-shadow .2s;
  position: relative;
}
.km-notice:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(255,77,141,.14); }
.km-notice--unread {
  border-color: rgba(255,77,141,.35);
  box-shadow: 0 10px 24px rgba(255,77,141,.18), inset 0 1px 0 rgba(255,255,255,.9);
}
.km-notice--unread::before {
  content: ''; position: absolute; left: 0; top: 16px; bottom: 16px; width: 3px;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #ff4d8d, #a020f0);
}

.km-notice__head {
  display: flex; align-items: center; gap: 10px; margin-bottom: 8px;
}
.km-notice__ico {
  width: 30px; height: 30px; border-radius: 10px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
  box-shadow: 0 4px 10px rgba(255,77,141,.3);
  flex-shrink: 0;
}
.km-notice__title {
  margin: 0; flex: 1;
  font-size: 15px; font-weight: 800; color: #2a1a3f; letter-spacing: -.01em;
}
.km-notice__dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 0 3px rgba(255,77,141,.18);
  flex-shrink: 0;
}
.km-notice__body {
  margin: 0 0 10px;
  font-size: 14px; line-height: 1.55; color: #4a3d66;
  white-space: pre-line;
}
.km-notice__time {
  font-size: 11px; color: #8d7fa6; text-align: right;
  padding-top: 8px; border-top: 1px solid rgba(255,77,141,.08);
  font-weight: 500;
}

.km-empty { text-align: center; padding: 60px 20px; color: #6b5c85; }
.km-empty__emoji { font-size: 50px; margin-bottom: 10px; }
.km-empty h3 { margin: 0 0 6px; font-size: 17px; color: #2a1a3f; }
.km-empty p { margin: 0; font-size: 13px; }

::v-deep .van-pull-refresh__head * { color: #6b5c85; font-size: 13px; }
</style>
